// Fair-play watch dog panel. Used by BOTH dashboards:
//   <FairPlay />            -> local admin: flags/reports for their own country, read-only
//   <FairPlay superAdmin /> -> super admin: global view + refund / release / auto-hold switch
import React, { useState, useEffect, useCallback } from 'react';
import { card, StatCards, ReportsTable, FlagsTable, HeldTable, btn } from './FairPlayParts';

const API_BASE =
  process.env.REACT_APP_API_URL ||
  (typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'http://localhost:5001'
    : (typeof window !== 'undefined' ? window.location.origin : ''));

const FairPlay = ({ superAdmin = false }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [autoSeize, setAutoSeize] = useState(true);
  const [msg, setMsg] = useState('');

  const api = useCallback(async (path, method = 'GET', body) => {
    const r = await fetch(`${API_BASE}/api/fair-play${path}`, {
      method,
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
      body: body ? JSON.stringify(body) : undefined
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok || j.success === false) throw new Error(j.error || `Request failed (${r.status})`);
    return j;
  }, []);

  const load = useCallback(async () => {
    try {
      setError('');
      setData(await api('/overview'));
      if (superAdmin) setAutoSeize((await api('/settings')).autoSeize);
    } catch (e) { setError(e.message); }
  }, [api, superAdmin]);

  useEffect(() => { load(); }, [load]);

  const act = async (label, fn) => {
    if (!window.confirm(label)) return;
    setBusy(true); setMsg('');
    try { await fn(); setMsg('Done.'); await load(); } catch (e) { setMsg(`Failed: ${e.message}`); }
    setBusy(false);
  };

  if (error) return <div style={card}>⚠️ {error}</div>;
  if (!data) return <div style={card}>Loading fair-play data…</div>;

  return (
    <div className="fair-play-panel">
      <div style={card}>
        <h3 style={{ margin: '0 0 6px' }}>🛡️ Fair-Play Watch Dog {superAdmin ? '— all countries' : '— your country'}</h3>
        <p style={{ margin: 0, color: '#666', fontSize: 13 }}>
          Learns normal play from every game and flags players who help each other against the rest of the table
          (feeding discards, winning together, dropping out early, same network). One game never convicts — evidence builds over several games.
        </p>
        {superAdmin && (
          <label style={{ display: 'block', marginTop: 10, fontSize: 13 }}>
            <input type="checkbox" checked={autoSeize} disabled={busy}
              onChange={(e) => { const on = e.target.checked; act(`Turn automatic hold of looted winnings ${on ? 'ON' : 'OFF'}?`, () => api('/settings', 'POST', { autoSeize: on })); }} />{' '}
            Automatically hold looted winnings for <b>confirmed</b> collusion (you review and refund)
          </label>
        )}
      </div>

      {data.reports[0] && <StatCards today={data.reports[0]} />}
      {msg && <div style={{ ...card, background: '#f5f9ff' }}>{msg}</div>}

      {superAdmin && data.heldRecoveries.length > 0 && (
        <div style={card}><h4 style={{ marginTop: 0 }}>💰 Money held — awaiting your decision</h4>
          <HeldTable rows={data.heldRecoveries} busy={busy} act={act} api={api} /></div>
      )}

      <div style={card}><h4 style={{ marginTop: 0 }}>🚩 Recent flags</h4>
        <FlagsTable flags={data.recentFlags} superAdmin={superAdmin} busy={busy} act={act} api={api} /></div>

      <div style={card}><h4 style={{ marginTop: 0 }}>📅 Daily reports</h4>
        <ReportsTable reports={data.reports} />
        {superAdmin && <button disabled={busy} style={{ ...btn('#1976d2'), marginTop: 10 }} onClick={() => act("Rebuild today's reports now?", () => api('/reports/generate', 'POST', {}))}>Rebuild today's report</button>}
      </div>
    </div>
  );
};

export default FairPlay;
